import { useContext, useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import SimpleTable from '../../../SimpleTable';
import { QAStoreContext } from '../QAStore';
import { useCommodityTree } from '../../queryHooks/useQaItemsApis';

const loadingData = {
  thirdCountryDutyAdValorem: <Spinner animation="border" size="sm" />,
  thirdCountryDutySpecific: <Spinner animation="border" size="sm" />,
  duty: <Spinner animation="border" size="sm" />,
  quotas: <Spinner animation="border" size="sm" />,
  remedies: <Spinner animation="border" size="sm" />,
  vat: <Spinner animation="border" size="sm" />,
  excise: <Spinner animation="border" size="sm" />,
  customsTech: <Spinner animation="border" size="sm" />,
};

const TariffModal = ({
  commodityCode,
  origin,
  preference,
  lineNumber,
  handleClose,
  isVatLiable,
  thirdCountryAV,
  thirdCountrySpecific,
  dutyExpressionRate,
  Quotas,
  vatRate,
  additionalDuties,
  excise,
  customsTech,
}) => {
  const show = !!commodityCode;

  const qaStore = useContext(QAStoreContext);
  const [currentConfig] = qaStore.currentConfig;
  const visibleFields = currentConfig.visibleFields.tariffModal;

  const {
    data: commodityTreeResponse,
    isPending: commodityTreeLoading,
    isError: commodityTreeError,
  } = useCommodityTree(commodityCode);
  const commodityTree = commodityTreeResponse?.[0] || {};

  const [commodityDetails, setCommodityDetails] = useState({});
  const [commodityDetailsLoading, setCommodityDetailsLoading] = useState(false);
  const [commodityDetailsError, setCommodityDetailsError] = useState(false);

  useEffect(() => {
    const getDetails = async () => {
      try {
        setCommodityDetailsLoading(true);
        setCommodityDetailsError(false);
        const redCheck = <span className="text-decoration-underline text-danger">CHECK</span>;

        const mappedResponse = {
          thirdCountryDutyAdValorem: thirdCountryAV || '-',
          thirdCountryDutySpecific: thirdCountrySpecific || '-',
          duty: dutyExpressionRate || '-',
          quotas: Quotas ? redCheck : '-',
          remedies: additionalDuties ? redCheck : '-',
          vat: (isVatLiable && vatRate) || '0.00%',
          excise: excise ? redCheck : '-',
          customsTech: customsTech ? redCheck : '-',
        };
        setCommodityDetails(mappedResponse);
      } catch (error) {
        setCommodityDetailsError(error);
      } finally {
        setCommodityDetailsLoading(false);
      }
    };

    if (commodityCode && origin) {
      getDetails();
    }
  }, [commodityCode, isVatLiable, origin, preference]);

  const tableData = {
    headings: [
      {
        label: 'Third Country Duty (Ad Valorem)',
        key: 'thirdCountryDutyAdValorem',
        hide: !visibleFields.thirdCountryDutyAdValorem,
      },
      {
        label: 'Third Country Duty (Specific)',
        key: 'thirdCountryDutySpecific',
        hide: !visibleFields.thirdCountryDutySpecific,
      },
      {
        label: 'Duty - preferential rate',
        key: 'duty',
        hide: !visibleFields.duty,
      },
      {
        label: 'Quotas',
        key: 'quotas',
        hide: !visibleFields.quotas,
      },
      {
        label: 'Trade remedies, safeguards and retaliatory duty',
        key: 'remedies',
        hide: !visibleFields.remedies,
      },
      {
        label: 'VAT Rate',
        key: 'vat',
        hide: !visibleFields.vat,
      },
      {
        label: 'Excise',
        key: 'excise',
        hide: !visibleFields.excise,
      },
      {
        label: 'Customs Tech',
        key: 'customsTech',
        hide: !visibleFields.customsTech,
      },
    ],
    values: [commodityDetailsLoading ? loadingData : commodityDetails],
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered dialogClassName="qa-wide-modal">
      <Modal.Body>
        <Modal.Title>
          <h3>
            Tariff requirements:{' '}
            <Link
              className="h3"
              to={`https://www.trade-tariff.service.gov.uk/commodities/${commodityCode}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {commodityCode}
            </Link>
          </h3>
        </Modal.Title>
        <div className="d-flex w-100 justify-content-between">
          <div>Country of Origin: {origin}</div>
          <div>Line Item: {lineNumber}</div>
        </div>

        <div className="tariff-modal-sections">
          {commodityTreeLoading && <Spinner animation="border" />}

          {commodityTreeError && <div>Error Loading Commodity Tree</div>}

          {!commodityTreeLoading && !commodityTreeError && (
            <ul className="w-100">
              <li style={{ marginLeft: '0rem' }}>
                {commodityTree.commodityChapter}: {commodityTree.chapterDesc}
              </li>
              <li style={{ marginLeft: '1rem' }}>
                {commodityTree.commodityHeading}: {commodityTree.headingDesc}
              </li>
            </ul>
          )}
        </div>

        <div className="tariff-modal-section-heading">
          <span>Measure Type</span>
          <span>Identified for this country</span>
        </div>

        {commodityDetailsError ? (
          <div>Error Loading Commodity Details</div>
        ) : (
          <SimpleTable data={tableData} isHeaderRow={false} textAlign="right" />
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex w-100 justify-content-end">
          <button className="qa-button secondary small" onClick={handleClose}>
            OK
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TariffModal;
